import { BadgeCheck, Heart, MapPin, Share2, Star } from "lucide-react";
import { useState } from "react";
import type { ProviderMainCardProps } from "../../core/types/tsx/components/Profesional/ProviderMainCard.types";

// Definimos el tipo para las estadísticas que vienen del JSON

import { getImageUri } from "../../core/utils/imageLoad";

export const ProviderMainCard = ({
	name,
	description,
	rating,
	reviews,
	location,
	shareUrl,
	experience,
	stats,
	isFavorite = false,
	favoriteLoading = false,
	onToggleFavorite,
	avatarUrl,
}: ProviderMainCardProps) => {
	const displayRating = rating ?? "0.0";
	// Si reviews es un número, úsalo; si es un array, usa su longitud; si no, 0
	let displayReviews = 0;
	if (Array.isArray(reviews)) {
		displayReviews = reviews.length;
	} else if (typeof reviews === "number") {
		displayReviews = reviews;
	}

	// Helper para formatear el tiempo de respuesta
	const formatResponseTime = (minutes?: number) => {
		if (minutes === undefined || minutes === null) return "N/A";
		if (minutes < 60) return `${minutes} min`;
		const hours = Math.round(minutes / 60);
		return `${hours} ${hours === 1 ? "hora" : "horas"}`;
	};

	const handleShare = async () => {
		const url = shareUrl || window.location.href;
		const title = name || "Perfil de proveedor";
		try {
			if (navigator.share) {
				await navigator.share({ title, text: title, url });
				return;
			}
			await navigator.clipboard.writeText(url);
			alert("Enlace copiado");
		} catch (_error) {
			// Silencioso si el usuario cancela
		}
	};

	// Helper for fallback initials
	const getInitials = (name?: string) => {
		if (!name) return "--";
		const parts = name.trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) return "--";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
	};

	// Fallback logic for image
	const imageSrc = getImageUri(avatarUrl) || undefined;
	const [hasImageError, setHasImageError] = useState(false);
	const showImage = Boolean(imageSrc) && !hasImageError;

	return (
		<div className='w-full'>
			{/* Card Principal */}
			<div className='bg-white rounded-[20px] shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07),0_10px_20px_-2px_rgba(0,0,0,0.04)] border border-gray-100 p-4 sm:p-6'>
				{/* --- Sección Superior: Perfil --- */}
				<div className='flex flex-col gap-4 sm:flex-row sm:gap-6'>
					{/* Imagen */}
					{showImage ? (
						<img
							src={imageSrc}
							alt={`Foto de ${name || "Proveedor"}`}
							className='h-24 w-24 self-center rounded-xl object-cover shrink-0 sm:h-32 sm:w-32 sm:self-auto'
							onError={() => setHasImageError(true)}
						/>
					) : (
						<div className='h-24 w-24 self-center rounded-xl bg-slate-200 flex items-center justify-center text-2xl font-bold text-slate-500 shrink-0 sm:h-32 sm:w-32 sm:text-3xl sm:self-auto'>
							{getInitials(name)}
						</div>
					)}

					{/* Info Principal */}
					<div className='flex-1 flex flex-col justify-center min-w-0'>
						<div className='flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start'>
							{/* Nombre y Badge */}
							<div className='flex-1 min-w-0 sm:mr-4'>
								<h1 className='text-2xl sm:text-3xl font-medium text-slate-600 flex items-center gap-2 flex-wrap'>
									{name || "Proveedor"}
									<BadgeCheck className='text-primary w-6 h-6 fill-white shrink-0' />
								</h1>
								<h2 className='text-slate-500 mt-1 text-sm sm:text-base break-words'>
									{description || "Servicios profesionales"}
								</h2>
							</div>

							{/* Iconos de acción (Compartir/Like) */}
							<div className='flex gap-3 text-slate-400 shrink-0 self-end sm:self-auto'>
								<button
									type='button'
									className='cursor-pointer hover:text-slate-600 transition-colors'
									onClick={handleShare}
									aria-label='Compartir perfil'
								>
									<Share2 className='w-5 h-5' />
								</button>
								<button
									type='button'
									className={`cursor-pointer transition-colors ${
										isFavorite
											? "text-error-text"
											: "text-slate-400 hover:text-error-text"
									}`}
									aria-label={
										isFavorite
											? "Quitar de favoritos"
											: "Agregar a favoritos"
									}
									onClick={onToggleFavorite}
									disabled={favoriteLoading}
								>
									<Heart
										className={`w-5 h-5 ${isFavorite ? "fill-error-text" : ""}`}
									/>
								</button>
							</div>
						</div>

						{/* Fila de Ubicación y Rating */}
						<div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6 mt-3 sm:mt-4 text-slate-500 text-sm sm:text-base'>
							{/* Rating */}
							<div className='flex items-center gap-1 min-w-0'>
								<Star className='w-5 h-5 text-primary fill-primary' />
								<span className='font-bold text-slate-700 text-base sm:text-lg'>
									{displayRating}
								</span>
								<span className='text-slate-400 text-xs sm:text-sm'>
									({displayReviews} reseñas)
								</span>
							</div>

							{/* Ubicación */}
							<div className='flex items-start gap-1 min-w-0'>
								<MapPin className='w-5 h-5 text-slate-400 shrink-0' />
								<span className='text-sm sm:text-base break-words'>
									{location || "Ubicación no disponible"}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* --- Divisor --- */}
				<hr className='border-gray-200 my-6 sm:my-8' />

				{/* --- Sección Inferior: Estadísticas (DINÁMICA) --- */}
				<div className='grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 text-center divide-x-0 md:divide-x divide-slate-100'>
					{/* Item 1: Trabajos Realizados */}
					<div className='flex flex-col items-center p-2'>
						<span className='text-primary text-2xl sm:text-3xl font-normal'>
							{stats?.completedOrders ?? 0}
						</span>
						<span className='text-slate-400 text-sm mt-1 uppercase tracking-wide'>
							Trabajos
						</span>
					</div>

					{/* Item 2: Calificación */}
					<div className='flex flex-col items-center p-2'>
						<div className='flex items-center gap-1'>
							<span className='text-primary text-2xl sm:text-3xl font-normal'>
								{displayRating}
							</span>
							<Star className='w-5 h-5 text-primary fill-primary mb-1' />
						</div>
						<span className='text-slate-400 text-sm mt-1 uppercase tracking-wide'>
							Calificación
						</span>
					</div>

					{/* Item 3: Tiempo de Respuesta */}
					<div className='flex flex-col items-center p-2'>
						<span className='text-primary text-2xl sm:text-3xl font-normal'>
							{formatResponseTime(stats?.responseTime)}
						</span>
						<span className='text-slate-400 text-sm mt-1 uppercase tracking-wide'>
							Respuesta
						</span>
					</div>

					{/* Item 4: Años de Experiencia */}
					<div className='flex flex-col items-center p-2'>
						<span className='text-primary text-2xl sm:text-3xl font-normal'>
							{experience ?? 0}
							<span className='text-xl ml-1'>+</span>
						</span>
						<span className='text-slate-400 text-sm mt-1 uppercase tracking-wide'>
							Años Exp.
						</span>
					</div>
				</div>
			</div>
		</div>
	);
};
export type { ProviderStats } from "../../core/types/tsx/components/Profesional/ProviderMainCard.types";
